import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Formik, Form, ErrorMessage } from 'formik';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import * as Yup from 'yup';
import cx from 'classnames';
import FormikControl from '../FormikContainer/FormikControl';
import TextError from '../FormikContainer/TextError';
import PrimaryButton from '../PrimaryButton';
import Button from '../Button';
import styles from './SchoolEditorModal.module.scss';

interface Props {
  onHide: () => void;
  show: boolean;
  onUpdate: (schoolInfo: {
    schoolName: string;
    country: string;
    schoolState: string;
    schoolCity: string;
    schoolZip: string;
  }) => void;
  schoolName: string;
  className: string;
}

interface FormValues {
  schoolName: string;
  country: string;
  schoolState: string;
  schoolCity: string;
  schoolZip: string;
}

const ValidationSchema = Yup.object().shape({
  schoolName: Yup.string().required('Please input a valid school name'),
  country: Yup.string().required('Required'),
  schoolState: Yup.string().required('Required'),
  schoolCity: Yup.string().required('Please input a valid city'),
  schoolZip: Yup.string().required('Please input a valid postal code'),
});

const SchoolEditorModal = (props: Props) => {
  const [schoolName, setSchoolName] = useState('');
  const [country, setCountry] = useState('United States');
  const [regionOrProvinceLabel, setRegionOrProvinceLabel] = useState('State');
  const [regionOrProvince, setRegionOrProvince] = useState('');
  const [city, setCity] = useState('');
  const [zipOrPostal, setZipOrPostal] = useState('');
  const [zipOrPostalCodeLabel, setZipOrPostalCodeLabel] = useState('Zip Code');
  const [zipOrPostalCodePlaceholder, setZipOrPostalCodePlaceholder] = useState('NNNNN');

  const initialValues: FormValues = {
    schoolName: '' || schoolName,
    country: '' || country,
    schoolState: regionOrProvince,
    schoolCity: city,
    schoolZip: zipOrPostal,
  };

  const onCountryChange = (value: string, setFieldValue: (field: string, value: string) => void) => {
    setCountry(value);
    setFieldValue('schoolState', '');
    setFieldValue('schoolCity', '');
    setFieldValue('schoolZip', '');

    if (value === 'United States') {
      setRegionOrProvinceLabel('State');
      setZipOrPostalCodeLabel('Zip Code');
      setZipOrPostalCodePlaceholder('NNNNN');
    } else if (value === 'Canada') {
      setRegionOrProvinceLabel('Province');
      setZipOrPostalCodeLabel('Postal Code');
      setZipOrPostalCodePlaceholder('XNX NXN');
    } else {
      setRegionOrProvinceLabel('Province');
      setZipOrPostalCodeLabel('Postal Code');
      setZipOrPostalCodePlaceholder('');
    }
  };

  const onRegionOrProvinceChange = (value: string) => {
    setRegionOrProvince(value);
  };

  const onCityChange = (value: string) => {
    setCity(value);
  };

  const onZipOrPostalChange = (value: string) => {
    setZipOrPostal(value);
  };

  const handleSubmit = (values: FormValues) => {
    const { schoolName, country, schoolState, schoolCity, schoolZip } = values;

    const SchoolInfo = {
      schoolName: schoolName,
      country: country,
      schoolState: schoolState,
      schoolCity: schoolCity,
      schoolZip: schoolZip,
    };

    props.onUpdate(SchoolInfo);
  };

  const handleOnHide = () => {
    props.onHide();
  };

  useEffect(() => {
    if (schoolName !== props.schoolName) {
      setSchoolName(props.schoolName);
    }
  }, [props.schoolName, schoolName]);

  return (
    <Modal className={styles.modal} dialogClassName='ModalDialog' onHide={handleOnHide} show={props.show}>
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitleContainer}>
          <h3 className={styles.modalTitle}>Edit School</h3>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <Formik initialValues={initialValues} validationSchema={ValidationSchema} onSubmit={handleSubmit}>
          {({ handleChange, setFieldValue }) => {
            return (
              <Form>
                <FormikControl
                  control='input'
                  type='text'
                  label='School Name'
                  name='schoolName'
                  placeholder='School Name'
                />
                <label className={cx('control-label formLabel', styles.customLabel)} style={{ paddingLeft: '5px' }}>
                  Country
                </label>
                <CountryDropdown
                  id='countryPicker'
                  classes={cx('form-control', styles.selectors)}
                  defaultOptionLabel='Select a Country'
                  name='country'
                  value={country}
                  onChange={(value, event) => {
                    handleChange(event);
                    onCountryChange(value, setFieldValue);
                  }}
                />
                <div className={styles.errorHintContainer}>
                  <ErrorMessage name='country' component={TextError} />
                </div>
                <label className={cx('control-label formLabel', styles.customLabel)} style={{ paddingLeft: '5px' }}>
                  {regionOrProvinceLabel}
                </label>
                <RegionDropdown
                  id='regionPicker'
                  classes={cx('form-control', styles.selectors)}
                  defaultOptionLabel={`Select ${regionOrProvinceLabel}`}
                  country={country}
                  name='schoolState'
                  value={regionOrProvince}
                  onChange={(value, event) => {
                    handleChange(event);
                    onRegionOrProvinceChange(value);
                  }}
                />
                <div className={styles.errorHintContainer}>
                  <ErrorMessage name='schoolState' component={TextError} />
                </div>
                <FormikControl
                  control='input'
                  type='text'
                  name='schoolCity'
                  label='City'
                  placeholder='City'
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(event);
                    onCityChange(event.target.value);
                  }}
                />
                <FormikControl
                  control='input'
                  type='text'
                  name='schoolZip'
                  label={zipOrPostalCodeLabel}
                  placeholder={zipOrPostalCodePlaceholder}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(event);
                    onZipOrPostalChange(event.target.value);
                  }}
                />
                <div className={styles.buttonsContainer}>
                  <PrimaryButton type='submit' small>
                    submit
                  </PrimaryButton>
                  <Button type='button' textBlue small onClick={handleOnHide}>
                    back
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default SchoolEditorModal;
